import React, { useState } from "react";
import {
  DndContext,
  useDroppable,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  DragOverEvent,
  useSensor,
  useSensors,
  PointerSensor,
  UniqueIdentifier,
} from "@dnd-kit/core";
import {
  useEditorStore,
  type EditorField,
  selectIsDirty,
} from "../store/useEditorStore";
import { FormService } from "../services/form.service";
import { useStore } from "zustand";
import { NavigationBlocker } from "../components/common/NavigationBlocker";
import { DesignPanel } from "../components/editor/panels/DesignPanel";
import { FieldsPanel } from "../components/editor/panels/FieldsPanel";
import { FieldSettingsPanel } from "../components/editor/panels/FieldSettingsPanel";
import { ComponentStylePanel } from "../components/editor/panels/ComponentStylePanel";
import { FormPreview } from "../components/editor/preview/FormPreview";
import { FieldPreview } from "../components/editor/FieldPreview";
import { EditorNavHeader } from "../components/common/EditorNavHeader";
import { useShopifySaveBar } from "../hooks/useShopifySaveBar";

const DEFAULT_PLACEHOLDER_BY_KEY: Record<string, string> = {
  first_name: "Ingresa tu nombre",
  last_name: "Ingresa tu apellido",
  email: "tucorreo@ejemplo.com",
  phone: "Tu número de teléfono",
  address_1: "Ingresa tu dirección",
  address_2: "Apartamento, suite, unidad, etc.",
  zip: "Código postal",
  city: "Ciudad",
  country_code: "Código de país",
};

const DEFAULT_PLACEHOLDER_BY_TYPE: Partial<
  Record<EditorField["type"], string>
> = {
  text: "Ingresa un Placeholder",
  number: "0",
  email: "tucorreo@ejemplo.com",
  tel: "Tu número de teléfono",
};

type DragItemPayload = {
  isSidebarItem?: boolean;
  type: string;
  label: string;
  systemKey?: string;
  id?: UniqueIdentifier;
  [key: string]: unknown;
};

function DroppablePreview({ children }: { children: React.ReactNode }) {
  const { setNodeRef, isOver } = useDroppable({
    id: "canvas-preview",
  });

  return (
    <div
      ref={setNodeRef}
      style={{
        borderWidth: isOver ? "3px" : "0px",
        borderStyle: "dashed",
        borderColor: isOver ? "#008060" : "transparent",
      }}
      className={`flex flex-col items-center justify-start transition-all duration-300 ease-in-out ${
        isOver ? "scale-[1.01]" : ""
      }`}
    >
      {children}
    </div>
  );
}

const VIEW_MODE_WIDTHS = {
  desktop: "w-full max-w-2xl",
  mobile: "w-[375px]",
};

export const CanvasLayout: React.FC = () => {
  const fields = useEditorStore((state) => state.config.fields);
  const viewMode = useEditorStore((state) => state.ui.viewMode);
  const setViewMode = useEditorStore((state) => state.setViewMode);
  const editorMode = useEditorStore((state) => state.ui.editorMode);
  const setEditorMode = useEditorStore((state) => state.setEditorMode);
  const selectedFieldId = useEditorStore((state) => state.ui.selectedFieldId);
  const selectedComponentId = useEditorStore(
    (state) => state.ui.selectedComponentId
  );
  const setSelectedField = useEditorStore((state) => state.setSelectedField);
  const setSelectedComponent = useEditorStore(
    (state) => state.setSelectedComponent
  );
  const [activeTab, setActiveTab] = useState<"fields" | "design">("fields");

  const isDirty = useEditorStore(selectIsDirty);
  const saveBarActive = isDirty && editorMode === "edit";
  const isSaving = useEditorStore((state) => state.ui.isSaving);
  const saveForm = useEditorStore((state) => state.saveForm);
  const discardChanges = useEditorStore((state) => state.discardChanges);

  const loadForm = useEditorStore((state) => state.loadForm);
  const [isLoading, setIsLoading] = React.useState(true);
  const [fetchError, setFetchError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let isMounted = true;
    async function fetchFormData() {
      try {
        setIsLoading(true);
        const config = await FormService.getForm();
        if (isMounted) {
          loadForm(config);
          setIsLoading(false);
        }
      } catch (err) {
        if (isMounted) {
          setFetchError(
            err instanceof Error ? err.message : "Failed to load form config"
          );
          setIsLoading(false);
        }
      }
    }
    fetchFormData();
    return () => {
      isMounted = false;
    };
  }, [loadForm]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );
  const [activeDragItem, setActiveDragItem] =
    React.useState<DragItemPayload | null>(null);
  const [activeDragId, setActiveDragId] =
    React.useState<UniqueIdentifier | null>(null);
  const [dropInsertIndex, setDropInsertIndex] = React.useState<number | null>(
    null
  );

  const temporal = useEditorStore.temporal;
  const canUndo = useStore(temporal, (state) => state.pastStates.length > 0);
  const canRedo = useStore(temporal, (state) => state.futureStates.length > 0);
  const undo = () => {
    if (temporal) temporal.getState().undo();
  };
  const redo = () => {
    if (temporal) temporal.getState().redo();
  };

  const handleDragStart = (event: DragStartEvent) => {
    if (event.active.data.current) {
      const payload = event.active.data.current as DragItemPayload;
      setActiveDragItem(payload);
      setActiveDragId(event.active.id);
      if (payload.isSidebarItem) {
        setDropInsertIndex(fields.length);
      }
    }
  };

  const moveField = useEditorStore((state) => state.moveField);
  const insertField = useEditorStore((state) => state.insertField);

  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event;
    const payload = active.data.current as DragItemPayload;
    if (!payload?.isSidebarItem) return;

    if (!over) {
      setDropInsertIndex(null);
      return;
    }

    if (over.id === "canvas-preview") {
      setDropInsertIndex(fields.length);
      return;
    }

    const overIndex = fields.findIndex((f) => f.tempId === over.id);
    if (overIndex === -1) {
      setDropInsertIndex(fields.length);
      return;
    }

    // Decide insert position using live drag rect midpoint (updates while dragging).
    const activeRect = active.rect.current.translated;
    const overRect = over.rect;
    if (activeRect && overRect) {
      const cursorY = activeRect.top + activeRect.height / 2;
      const midpointY = overRect.top + overRect.height / 2;
      const isAfter = cursorY > midpointY;
      setDropInsertIndex(isAfter ? overIndex + 1 : overIndex);
      return;
    }

    setDropInsertIndex(overIndex);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveDragItem(null);
    setActiveDragId(null);
    const currentInsertIndex = dropInsertIndex;
    setDropInsertIndex(null);
    if (!over) return;
    const payload = active.data.current as DragItemPayload;

    if (payload?.isSidebarItem) {
      const isOverCanvas = over.id === "canvas-preview";
      const isOverField = fields.some((f) => f.tempId === over.id);
      if (isOverCanvas || isOverField) {
        const computedIndex =
          currentInsertIndex == null ? fields.length : currentInsertIndex;
        const newIndex = Math.min(Math.max(computedIndex, 0), fields.length);
        const fieldType = payload.type as Exclude<
          EditorField["type"],
          "offer_embed"
        >;
        const defaultPlaceholder =
          (payload.systemKey &&
            DEFAULT_PLACEHOLDER_BY_KEY[payload.systemKey]) ||
          DEFAULT_PLACEHOLDER_BY_TYPE[fieldType];
        const newField: EditorField = {
          tempId: crypto.randomUUID(),
          type: fieldType,
          key: payload.systemKey || `${payload.type}_${Date.now()}`,
          label: payload.label,
          display_order: newIndex,
          visible: true,
          required: false,
          locked: false,
          style: { width: "100%" },
          ...(defaultPlaceholder && { placeholder: defaultPlaceholder }),
        };
        insertField(newField, newIndex);
      }
    } else if (active.id !== over.id) {
      const oldIndex = fields.findIndex((f) => f.tempId === active.id);
      const newIndex = fields.findIndex((f) => f.tempId === over.id);
      if (oldIndex !== -1 && newIndex !== -1) moveField(oldIndex, newIndex);
    }
  };

  const handleClearSelection = () => {
    setSelectedField(null);
    setSelectedComponent(null);
  };

  useShopifySaveBar({
    isDirty: saveBarActive,
    isSaving,
    onSave: saveForm,
    onDiscard: discardChanges,
  });

  if (isLoading)
    return (
      <div className="flex flex-col h-screen bg-[#f1f1f1]">
        <EditorNavHeader />
        <s-page heading="Editor de Formulario">
          <s-box padding="large-400">
            <s-stack direction="block" gap="base" alignItems="center">
              <s-spinner />
              <s-text color="subdued">Cargando configuración...</s-text>
            </s-stack>
          </s-box>
        </s-page>
      </div>
    );

  if (fetchError)
    return (
      <div className="flex flex-col h-screen bg-[#f1f1f1]">
        <EditorNavHeader />
        <s-page heading="Editor de Formulario">
          <s-banner tone="critical">
            <s-text>Error al cargar: {fetchError}</s-text>
          </s-banner>
        </s-page>
      </div>
    );

  const isInspectingItem = !!(selectedFieldId || selectedComponentId);

  // 1. Extraemos las herramientas a la izquierda
  const leftTools = (
    <div className="bg-white p-1 rounded-full shadow-sm border border-gray-200/50 flex items-center gap-1 transition-all">
      <div className="flex items-center gap-1 bg-gray-50 p-1 rounded-full">
        <button
          onClick={() => setViewMode("desktop")}
          className={`p-1.5 rounded-full transition-all ${viewMode === "desktop" ? "bg-white shadow-sm text-[#008060]" : "text-gray-500 hover:text-gray-700"}`}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect width="20" height="14" x="2" y="3" rx="2" />
            <line x1="8" x2="16" y1="21" y2="21" />
            <line x1="12" x2="12" y1="17" y2="21" />
          </svg>
        </button>
        <button
          onClick={() => setViewMode("mobile")}
          className={`p-1.5 rounded-full transition-all ${viewMode === "mobile" ? "bg-white shadow-sm text-[#008060]" : "text-gray-500 hover:text-gray-700"}`}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect width="14" height="20" x="5" y="2" rx="2" ry="2" />
            <path d="M12 18h.01" />
          </svg>
        </button>
      </div>

      <div className="w-px h-5 bg-gray-200 mx-1" />

      <div className="flex items-center gap-1 bg-gray-50 p-1 rounded-full mr-1">
        <button
          onClick={undo}
          disabled={!canUndo}
          className={`p-1.5 rounded-full transition-all ${!canUndo ? "text-gray-300 cursor-not-allowed" : "text-gray-600 hover:bg-white hover:shadow-sm"}`}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M3 7v6h6" />
            <path d="M21 17a9 9 0 0 0-9-9 9 9 0 0 0-6 2.3L3 13" />
          </svg>
        </button>
        <button
          onClick={redo}
          disabled={!canRedo}
          className={`p-1.5 rounded-full transition-all ${!canRedo ? "text-gray-300 cursor-not-allowed" : "text-gray-600 hover:bg-white hover:shadow-sm"}`}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M21 7v6h-6" />
            <path d="M3 17a9 9 0 0 1 9-9 9 9 0 0 1 6 2.3L21 13" />
          </svg>
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex flex-col h-screen overflow-hidden bg-[#f1f1f1]">
      <EditorNavHeader leftContent={leftTools}>
        {isDirty ? (
          <span className="bg-white/60 border border-yellow-200 text-yellow-700 text-[10px] font-bold px-3 py-1.5 rounded-full uppercase tracking-wider flex items-center gap-1.5 shadow-sm">
            <span className="w-1.5 h-1.5 bg-yellow-500 rounded-full animate-pulse"></span>
            Unsaved
          </span>
        ) : (
          <span className="bg-white/60 border border-green-200 text-green-700 text-[10px] font-bold px-3 py-1.5 rounded-full uppercase tracking-wider flex items-center gap-1.5 shadow-sm">
            <span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>
            Saved
          </span>
        )}

        <div className="w-80 lg:w-[380px] flex items-center bg-white p-1 rounded-full shadow-sm border border-gray-200/50">
          <button
            onClick={() => setEditorMode("edit")}
            className={`flex-1 text-center py-2 text-sm font-medium rounded-full transition-all ${editorMode === "edit" ? "bg-gray-100 text-[#008060] shadow-sm" : "text-gray-500 hover:text-gray-900"}`}
          >
            Modo de Edición
          </button>
          <button
            onClick={() => setEditorMode("preview")}
            className={`flex-1 text-center py-2 text-sm font-medium rounded-full transition-all ${editorMode === "preview" ? "bg-gray-100 text-[#008060] shadow-sm" : "text-gray-500 hover:text-gray-900"}`}
          >
            Modo de Vista Previa
          </button>
        </div>
      </EditorNavHeader>

      <DndContext
        sensors={sensors}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
      >
        <NavigationBlocker isDirty={isDirty} />

        {isSaving && (
          <div className="absolute inset-0 bg-white/50 z-[100] flex items-center justify-center cursor-wait backdrop-blur-sm transition-all" />
        )}

        {/* Área Principal: Canvas + Inspector */}
        <div className="flex-1 overflow-y-auto pb-8">
          <s-page heading="Editor de Formulario">
            <div className="flex items-start justify-center gap-2 lg:gap-5">
              <div
                className="flex-1 w-full flex flex-col items-center transition-all relative pb-32"
                onClick={handleClearSelection}
              >
                <div
                  className={`${VIEW_MODE_WIDTHS[viewMode]} transition-all duration-300 relative`}
                >
                  <DroppablePreview>
                    <FormPreview
                      dropInsertIndex={
                        activeDragItem?.isSidebarItem ? dropInsertIndex : null
                      }
                    />
                  </DroppablePreview>
                </div>
              </div>

              {/* DYNAMIC INSPECTOR PANEL */}
              {editorMode === "edit" && (
                <div className="w-80 lg:w-[380px] bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.06)] border border-gray-100 flex flex-col shrink-0 sticky top-3 overflow-hidden z-10 h-[calc(100vh-210px)] transition-all">
                  {/* Header Inspector */}
                  <div className="flex-none p-3 border-b border-gray-100 bg-gray-50/30">
                    {isInspectingItem ? (
                      <button
                        onClick={handleClearSelection}
                        className="flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-[#008060] transition-colors px-3 py-2 rounded-xl hover:bg-indigo-50/50 w-full group"
                      >
                        <div className="p-1 bg-white rounded-md shadow-sm border border-gray-100 group-hover:border-indigo-200 transition-colors">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="m15 18-6-6 6-6" />
                          </svg>
                        </div>
                        Volver a {activeTab === "fields" ? "Campos" : "Diseño"}
                      </button>
                    ) : (
                      <div className="flex text-sm font-medium bg-gray-100/80 p-1 rounded-xl">
                        <button
                          onClick={() => setActiveTab("fields")}
                          className={`flex-1 text-center py-2 rounded-lg transition-all duration-200 ${activeTab === "fields" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
                        >
                          Añadir Campos
                        </button>
                        <button
                          onClick={() => setActiveTab("design")}
                          className={`flex-1 text-center py-2 rounded-lg transition-all duration-200 ${activeTab === "design" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
                        >
                          Diseño Global
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Contenido con Scroll */}
                  <div className="flex-1 overflow-y-auto overflow-x-hidden px-3 pb-3 pt-2 custom-scrollbar">
                    {isInspectingItem ? (
                      <div className="animate-in fade-in slide-in-from-right-4 duration-300 -mt-1">
                        {selectedFieldId && (
                          <FieldSettingsPanel key={selectedFieldId} />
                        )}
                        {selectedComponentId && <ComponentStylePanel />}
                      </div>
                    ) : (
                      <div className="animate-in fade-in duration-300">
                        <div
                          className={
                            activeTab === "fields" && !isInspectingItem
                              ? "block"
                              : "hidden"
                          }
                        >
                          <FieldsPanel />
                        </div>
                        <div
                          className={
                            activeTab === "design" && !isInspectingItem
                              ? "block"
                              : "hidden"
                          }
                        >
                          <DesignPanel />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </s-page>
        </div>

        <DragOverlay dropAnimation={null}>
          {activeDragItem &&
            (activeDragItem.isSidebarItem ? (
              <div className="p-3 border rounded-xl bg-white shadow-2xl cursor-grabbing border-indigo-500 opacity-95 flex items-center gap-3 w-56 scale-105 transition-transform pointer-events-none">
                <div className="p-1.5 bg-indigo-50 text-[#008060] rounded-md">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="9" cy="12" r="1" />
                    <circle cx="9" cy="5" r="1" />
                    <circle cx="9" cy="19" r="1" />
                    <circle cx="15" cy="12" r="1" />
                    <circle cx="15" cy="5" r="1" />
                    <circle cx="15" cy="19" r="1" />
                  </svg>
                </div>
                <span className="font-medium text-gray-800">
                  {activeDragItem.label}
                </span>
              </div>
            ) : (
              (() => {
                const draggedField = fields.find(
                  (f) => f.tempId === activeDragId
                );
                if (!draggedField) return null;
                return (
                  <div
                    className="bg-white rounded-xl shadow-2xl ring-2 ring-indigo-400 p-1 cursor-grabbing opacity-90 scale-[1.02] transition-transform pointer-events-none"
                    style={{ width: draggedField.style?.width || "100%" }}
                  >
                    <FieldPreview field={draggedField} />
                  </div>
                );
              })()
            ))}
        </DragOverlay>
      </DndContext>
    </div>
  );
};
